import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Snackbar,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SchoolIcon from "@mui/icons-material/School";
import {
  getCompliancePipeline,
  moveDriverOnboardingStep,
} from "api/onboardingApi";
import { unwrapList, unwrapObject } from "api/unwrap";
import BulkAssignTrainingDialog from "./BulkAssignTrainingDialog";

/**
 * Onboarding Pipeline Kanban (admin).
 *
 * Columns = OnboardingStep enum values, oldest-in-step at top.
 * Drag a card from one column to another to manually move a driver.
 * Direct moves to READY_TO_DRIVE / REJECTED are intentionally blocked by
 * the backend — those go through the per-driver compliance page where the
 * admin captures the readiness check or rejection reason.
 */

const COLUMNS = [
  { key: "DOCS_UPLOAD", label: "Docs Upload", color: "#0ea5e9" },
  { key: "BACKGROUND_PENDING", label: "Background", color: "#a855f7" },
  { key: "TRAINING_IN_PROGRESS", label: "Training", color: "#f97316" },
  { key: "DRUG_TEST_PENDING", label: "Drug Test", color: "#facc15" },
  { key: "VEHICLE_INSPECTION_PENDING", label: "Vehicle Inspection", color: "#14b8a6" },
  { key: "ESIGN_PENDING", label: "E-Sign", color: "#8b5cf6" },
  { key: "FINAL_REVIEW", label: "Final Review", color: "#3b82f6" },
  { key: "ONBOARDING_HOLD", label: "On Hold", color: "#94a3b8" },
  { key: "READY_TO_DRIVE", label: "Ready", color: "#16a34a" },
  { key: "REJECTED", label: "Rejected", color: "#dc2626" },
];

const DRAG_BLOCKED_TARGETS = new Set(["READY_TO_DRIVE", "REJECTED"]);

const Card_ = ({ card, onClick, onDragStart, dragging }) => (
  <Box
    draggable
    onDragStart={(e) => onDragStart(e, card)}
    onClick={onClick}
    sx={{
      cursor: "grab",
      backgroundColor: "#ffffff",
      borderRadius: 1.5,
      border: "1px solid #e2e8f0",
      p: 1.5,
      mb: 1,
      opacity: dragging ? 0.4 : 1,
      "&:hover": { borderColor: "#1F4E78", boxShadow: 1 },
      "&:active": { cursor: "grabbing" },
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
      {card.fullName}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
      {card.email || card.phone || "—"}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
      <Chip
        size="small"
        label={`${card.completionPercent || 0}%`}
        sx={{
          height: 18,
          fontSize: 11,
          fontWeight: 600,
          backgroundColor: "#f1f5f9",
          color: "#475569",
        }}
      />
      <Typography
        variant="caption"
        color={card.daysInStep > 7 ? "error.main" : "text.secondary"}
        sx={{ fontWeight: card.daysInStep > 7 ? 600 : 400 }}
      >
        {card.daysInStep === 0
          ? "today"
          : card.daysInStep === 1
          ? "1 day in step"
          : `${card.daysInStep} days in step`}
      </Typography>
    </Box>
  </Box>
);

const Column = ({ col, cards, onDrop, onDragOver, onDragLeave, hovered, onCardDragStart, onCardClick, draggingId }) => (
  <Box
    onDrop={(e) => onDrop(e, col.key)}
    onDragOver={(e) => onDragOver(e, col.key)}
    onDragLeave={(e) => onDragLeave(e, col.key)}
    sx={{
      minWidth: 260,
      maxWidth: 280,
      flex: "0 0 auto",
      borderRadius: 2,
      backgroundColor: hovered ? "#e0f2fe" : "#f8fafc",
      border: hovered ? "2px dashed #0ea5e9" : "1px solid #e2e8f0",
      p: 1.5,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: col.color,
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
        {col.label}
      </Typography>
      <Chip
        size="small"
        label={cards.length}
        sx={{ height: 18, fontSize: 11, fontWeight: 700, ml: "auto" }}
      />
    </Box>
    {DRAG_BLOCKED_TARGETS.has(col.key) ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontStyle: "italic" }}>
        Move via per-driver page
      </Typography>
    ) : null}
    {cards.length === 0 ? (
      <Box
        sx={{
          textAlign: "center",
          color: "#94a3b8",
          py: 2,
          fontSize: 12,
        }}
      >
        empty
      </Box>
    ) : (
      cards.map((c) => (
        <Card_
          key={c.driverDetailsId}
          card={c}
          onClick={() => onCardClick(c)}
          onDragStart={onCardDragStart}
          dragging={draggingId === c.driverDetailsId}
        />
      ))
    )}
  </Box>
);

export const OnboardingPipelinePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedStep = searchParams.get("step");
  const [board, setBoard] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCol, setHoveredCol] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const allDrivers = React.useMemo(() => {
    const out = [];
    COLUMNS.forEach((col) => {
      (board[col.key] || []).forEach((c) => out.push(c));
    });
    return out;
  }, [board]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCompliancePipeline();
      setBoard(unwrapObject(res) || {});
    } catch (e) {
      setError(e?.message || "Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCardDragStart = (e, card) => {
    setDraggingId(card.driverDetailsId);
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({ driverId: card.driverDetailsId, from: card.currentStep }),
    );
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, col) => {
    if (DRAG_BLOCKED_TARGETS.has(col)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (hoveredCol !== col) setHoveredCol(col);
  };

  const handleDragLeave = (e, col) => {
    if (hoveredCol === col) setHoveredCol(null);
  };

  const handleDrop = async (e, targetCol) => {
    e.preventDefault();
    setHoveredCol(null);
    setDraggingId(null);
    if (DRAG_BLOCKED_TARGETS.has(targetCol)) return;
    let payload;
    try {
      payload = JSON.parse(e.dataTransfer.getData("application/json"));
    } catch {
      return;
    }
    if (!payload?.driverId || payload.from === targetCol) return;

    // Optimistic update
    const prev = board;
    const fromList = (prev[payload.from] || []).filter(
      (c) => c.driverDetailsId !== payload.driverId,
    );
    const card = (prev[payload.from] || []).find((c) => c.driverDetailsId === payload.driverId);
    if (!card) return;
    const next = {
      ...prev,
      [payload.from]: fromList,
      [targetCol]: [...(prev[targetCol] || []), { ...card, currentStep: targetCol }],
    };
    setBoard(next);

    try {
      await moveDriverOnboardingStep(payload.driverId, { step: targetCol });
      setToast(`Moved ${card.fullName} → ${targetCol}`);
    } catch (err) {
      setBoard(prev); // rollback
      setError(err?.response?.data?.message || err?.message || "Move failed");
    }
  };

  return (
    <Stack
      spacing={2}
      sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Onboarding Pipeline
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Drag cards between columns to move drivers through onboarding. Click a card to open the driver detail.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<SchoolIcon />}
            onClick={() => setBulkOpen(true)}
            sx={{ backgroundColor: "#1F4E78" }}
            disabled={allDrivers.length === 0}
          >
            Bulk Assign Training
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      {focusedStep ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={`Filtered: ${COLUMNS.find((c) => c.key === focusedStep)?.label || focusedStep}`}
            color="primary"
            size="small"
            onDelete={() => setSearchParams({})}
          />
          <Typography variant="caption" color="text.secondary">
            showing only this step — click × to see all columns
          </Typography>
        </Box>
      ) : null}

      {loading && Object.keys(board).length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 2 }}>
          {(focusedStep ? COLUMNS.filter((c) => c.key === focusedStep) : COLUMNS).map((col) => (
            <Column
              key={col.key}
              col={col}
              cards={board[col.key] || []}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              hovered={hoveredCol === col.key}
              onCardDragStart={handleCardDragStart}
              onCardClick={(c) => navigate(`/compliance/drivers/${c.driverDetailsId}`)}
              draggingId={draggingId}
            />
          ))}
        </Box>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast || ""}
      />

      <BulkAssignTrainingDialog
        open={bulkOpen}
        onClose={() => {
          setBulkOpen(false);
          load();
        }}
        drivers={allDrivers}
      />
    </Stack>
  );
};

export default OnboardingPipelinePage;
